import { STEP } from "#src/constants/step.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";
import { useEappStore } from "#src/stores/electronic-application.ts";
import { useStepNameStore } from "#src/stores/step-name.ts";
import { useFlowStore } from "#src/stores/flow.ts";

import { useReferralForm } from "#src/components/steps/referral/referral-form.composable.js";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed } from "vue";
import {
  type BaseStepStoreState,
  type ApiFlowStep,
} from "#src/types/StepStore.ts";
import { type InsuredType } from "#src/types/Insured.ts";

interface ReferralState extends BaseStepStoreState {}
interface ReferralStep extends ApiFlowStep, ToSetupState<ReferralState> {
  setStepRequirements(): void;
}

function initialState(): ReferralState {
  return {
    inactive: false,
  };
}

function initializeReferralStep(pinia: Pinia): ReferralStep {
  const state = generateInitialState<ReferralState>(initialState());

  function $reset() {
    resetState<ReferralState>(state, initialState());
  }

  const hideNoLicenseBanner = computed(() => true);
  const step = computed(() => STEP.REFERRAL);
  const next = computed(() => {
    const name = useStepNameStore(pinia);
    return name.step;
  });
  const previous = computed(() => null);
  const controlsNext = computed(() => {
    const eApp = useEappStore(pinia);
    return !eApp.refer && !eApp.id;
  });

  function setStepRequirements() {
    const flow = useFlowStore(pinia);
    state.inactive.value = !flow.initialStepIsReferral;
  }

  // NOTE(tim): type args correctly
  function generateForm(module: InsuredType, args: any) {
    return useReferralForm(pinia, module, args);
  }

  return {
    ...state,
    hideNoLicenseBanner,
    step,
    next,
    previous,
    controlsNext,
    setStepRequirements,
    $reset,
    generateForm,
  };
}

export const useStepReferralStore = (pinia: Pinia, hot?: StoreGeneric) => {
  return defineStore<string, ReferralStep>("step-referral", () => {
    return initializeReferralStep(pinia);
  })(pinia, hot);
};
